import type { ThreeDModelType } from '@api/use3dApi'
import use3dApi from '@api/use3dApi'
import { AppConfig } from '@config'
import type { SelectChangeEvent } from '@mui/material'
import { InputLabel, MenuItem, Select } from '@mui/material'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { ModelObject } from './ThreeDModelList'

type BaseProps = {
  label: string
  selectorConfig: {
    selectorModels: ModelObject[]
    modelType: ThreeDModelType
  }
  disabled?: boolean
}

const ModelSelect = styled(Select<string>)({
  marginBottom: 10
})

type ThreeDModelSelectorProps = BaseProps

export const noModelSelected = 'None'

export default function ThreeDModelSelector ({ selectorConfig, label, disabled = false }: ThreeDModelSelectorProps) {
  const getDefaultModelName = (modelType: ThreeDModelType) => {
    const modelNameFunctions = {
      main: AppConfig['3d'].main(),
      demo: AppConfig['3d'].demo()
    }

    return modelNameFunctions[modelType]
  }

  const [name, setName] = useState(getDefaultModelName(selectorConfig.modelType))
  const { saveModelName } = use3dApi()
  const { t } = useTranslation()

  const handleChange = (e: SelectChangeEvent) => {
    saveModelName(selectorConfig.modelType, e.target.value).then(() => setName(e.target.value))
  }

  return <Stack direction={'column'}>
    <InputLabel shrink id={selectorConfig.modelType}>
      {t(label)}
    </InputLabel>
    <ModelSelect
      id={selectorConfig.modelType}
      fullWidth
      value={name}
      size="small"
      margin="dense"
      type="text"
      variant="outlined"
      onChange={handleChange}
      disabled={disabled}
      defaultValue={noModelSelected}
    >
      <MenuItem value={noModelSelected}>{t(noModelSelected)}</MenuItem>
      {selectorConfig.selectorModels.map(({ name, id }) => <MenuItem key={`3d_model_${id}`} value={name}>
        {name}
      </MenuItem>)}
    </ModelSelect>
  </Stack>
}
